import { Injectable } from '@angular/core';
import { map, switchMap, take } from 'rxjs/operators';

//* Core
import { EmployeePackage } from '@core/models';
import { EmployeeProfileService } from '@core/services';

//* Shared
import { CardTitle } from '@shared/enums';
import { CardContent, CardData } from '@shared/models';

//* Base
import { Card } from './constants/card';
import * as utils from './utils/profile.utils';
import { getBclStatus } from '@core/services/constants';
import { Observable, forkJoin, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { convertToDollars } from '@shared/utils';

interface GenericObj {
  [key: string]: any;
}

// import { ProfileModule } from './profile.module';

@Injectable({ providedIn: 'root' })
export class ProfileService {
  /*** Observable which holds view-ready profile card data for each Profile Card template */
  cards$ = this.profileService.employeeProfile$.pipe(map(profile => this.buildCardData(profile)));

  /*** Observable which determines to show Paper Check Only Bank Card  or Normal Bank Card*/
  isPaperCheckOnly$ = this.profileService.employeeProfile$.pipe(
    map(profile => !profile.bankAccounts.length)
  );
  editPersonalAllowed$ = this.profileService.employeeProfile$.pipe(
    map(profile => profile.personal.portalEditAllowed)
  );
  constructor(private profileService: EmployeeProfileService, private http: HttpClient) {}

  /**
   ** Method which builds each Profile Card,
   ** ***IN ORDER OF DESIGN LAYOUT***.
   * 1. Create Profile Selector Variables for reducing redundancy.
   * ___
   * 2. Clean each card type content data items to View-Ready format.
   * ___
   * a. Personal Card Content Items.
   *___
   * b. Address Card content items.
   *___
   * c. Contact Card content items.
   *___
   * d. Bank Account Card content items.
   *___
   * e. Federal Taxes Card content items.
   *___
   * f. State Taxes Card content items.
   *___
   * 3. Build a collection of each card type.
   *___
   * 4. Build View-Ready Card Content for each Collection.
   *___
   * a. Function to combine Card Labels & Card Data for each card's content collection.
   *___
   * b. Store each card's content collection of {label, info} into Card Class.
   *___
   * 5. Build Each Card with View-Ready data into a collection of Cards.
   *___
   * 6. Store Bank Card (has different template for now) into place within Card UI Layout.
   *___
   */
  private buildCardData(profile: EmployeePackage): CardData[] {
    //#region //* 1. Data selectors.
    const personal = profile.personal;
    const bankAccounts = profile.bankAccounts;
    const fedTax = profile.federalTax;
    const stateTax = profile.stateTax;
    //#endregion

    //#region //* 2. Cleaning Card items.
    /*** combined personal name data for creation of a single string name. */
    const personalNames = [personal.firstName, personal.lastName, personal.suffix];

    //* a. Clean Personal Card Data
    const personalIs1099Contractor = personal.is1099Contractor;
    const personalBirthDate = utils.formatDate(personal.birthDate);
    const personalName = utils.formatNames(personalNames);
    const personalSex = utils.maleOrFemale(personal.sex);

    //* b. Clean Address Card Data
    const addressEnd = [`${personal.city},`, `${personal.state.toUpperCase()}`, personal.zip].join(
      ' '
    );
    const addressContent = [personal.address1, personal.address2, addressEnd];

    //* c. Clean Contact Card Data
    const contactPhones = {
      emergency: utils.formatPhoneNumber(personal.emergencyPhone),
      home: utils.formatPhoneNumber(personal.homePhone),
      mobile: utils.formatPhoneNumber(personal.mobilePhone),
    };

    const emergencyContact =
      personal.emergencyName && personal.emergencyPhone
        ? [personal.emergencyName, contactPhones.emergency]
        : null;

    //* d. Clean Bank Account Card Data
    const bankAccountsData = bankAccounts.map(bankData => {
      return {
        accountName: utils.accountNameOrNA(
          bankData.accountName,
          bankData.isRemainder,
          bankData.accountType
        ),
        accountNumber: bankData.accountNumber,
        accountPayMethod: bankData.payMethod,
        accountType: utils.formatAccountType(bankData.accountType),
        splitPay: utils.formatSplitAmount(
          bankData?.amount,
          bankData.depositType,
          bankData.isRemainder
        ),
      };
    });

    //* e. Clean Federal Taxes Card Data
    const fedTaxAllowances = fedTax?.allowances;
    const fedTaxClaimDependentsAmount = convertToDollars(fedTax?.claimDependentsAmount);
    const fedTaxDeductionAmount = convertToDollars(fedTax?.deductionsAmount);
    const fedTaxExtraWitholding = convertToDollars(fedTax?.extraWithholding);
    const fedTaxMultipleJobsOrSpouseWorks = utils.yesOrNoFromBoolean(fedTax?.hasMultipleJobs);
    const fedTaxOtherIncome = convertToDollars(fedTax?.otherIncomeAmount);
    const fedTaxStatus = fedTax?.filingStatus?.description;
    const fedTaxWithholdingTax = utils.taxExemptOrTaxable(fedTax?.withholdingTax);
    const fedTaxYear2019 = utils.is2019w4(fedTax?.w4FormVersion);

    //* f. Clean State Taxes Card Data
    const isNonTaxState = stateTax?.isNonTaxState;
    const isDollarAllowanceState = stateTax?.isDollarAllowanceState;
    const stateTaxAllowances =
      (!isNonTaxState && !isDollarAllowanceState && stateTax?.allowances) ||
      (isDollarAllowanceState && convertToDollars(stateTax?.stateDollarAllowance));

    const stateTaxExtraWitholding = !isNonTaxState && convertToDollars(stateTax?.extraWithholding);
    const stateTaxFilingState = utils.stateAndNoIncomeTax(
      stateTax?.incomeFilingState,
      isNonTaxState
    );
    const stateTaxFilingStatus = !isNonTaxState && stateTax?.filingStatus?.description;
    const stateTaxUnemploymentState = utils.UsStateAbbrvToName(stateTax?.unEmploymentFilingState);
    const stateTaxWitholdingTax =
      !stateTax?.isNonTaxState && utils.taxExemptOrTaxable(stateTax?.withholdingTax);

    //#endregion

    //#region //* 3. Building Card content collection.
    /*** View-Ready Profile Card data collection */

    const cardContent = {
      personal: [personalName, personal.ssn, personalBirthDate, personalSex],
      contact: [contactPhones.mobile, contactPhones.home, personal.email, emergencyContact],
      address: [...addressContent],
      bankAccounts: bankAccountsData,
      /** Federal Taxes Cleaned Card data.
       *
       ** Federal Taxes card has two versions, {2019, 2020+}.
       *
       ** They have different labels in Card Class and different content.
       */
      federalTaxes: {
        '2019': [fedTaxStatus, fedTaxExtraWitholding, fedTaxWithholdingTax, fedTaxAllowances],
        '2020+': [
          fedTaxStatus,
          fedTaxOtherIncome,
          fedTaxWithholdingTax,
          fedTaxDeductionAmount,
          fedTaxMultipleJobsOrSpouseWorks,
          fedTaxExtraWitholding,
          fedTaxClaimDependentsAmount,
        ],
      },
      stateTaxes: [
        stateTaxFilingState,
        stateTaxExtraWitholding,
        stateTaxFilingStatus,
        stateTaxUnemploymentState,
        stateTaxWitholdingTax,
        stateTaxAllowances,
      ],
    };
    //#endregion

    //#region //* 4. Formatting Card data collection.
    const formatCardContent = (
      labels: GenericObj,
      data: GenericObj,
      fedTaxYear: string
    ): CardContent[] => {
      //#region //* Helper Fns
      const fedTax = 'federalTaxes';
      const stateTax = 'stateTaxes';
      const bankAccounts = 'bankAccounts';
      const is1099 = personalIs1099Contractor;

      const getKeys = <T>(obj: T) => Object.keys(obj) as Array<keyof typeof CardTitle>;
      const keysToFilterFor1099 = [fedTax, stateTax];
      const filter1099 = (labelKey: string) =>
        is1099 && keysToFilterFor1099.includes(labelKey) ? null : labelKey;
      const labelKeys = getKeys(labels).filter(filter1099);
      //#endregion

      //#region //* Build Card Content Collection
      const buildContent = (key: string) => {
        //#region //? Helpers
        const isFedTax = key === fedTax;
        const isBankAccount = key === bankAccounts;
        const cardLabelSelectors = isFedTax ? labels[key][fedTaxYear] : labels[key];
        const cardDataSelectors = isFedTax ? data[key][fedTaxYear] : data[key];
        //#endregion

        //#region //? build Content Array
        return cardLabelSelectors
          .map((_: string, i: number) => {
            const cleanDataSelector = utils.cleanString(cardDataSelectors[i]);
            return cleanDataSelector && !isBankAccount
              ? {
                  label: cardLabelSelectors[i],
                  info: cleanDataSelector,
                }
              : cardDataSelectors[i];
          })
          .filter((cardContent: CardContent[]) => cardContent);
      };
      //#endregion

      return labelKeys.map(key => buildContent(key));
    };
    //#endregion
    //* b.
    // Card.portalEditAllowed = profile.personal.portalEditAllowed;
    Card.content = formatCardContent(Card.labels, cardContent, fedTaxYear2019);
    //#endregion

    //#region //* 5. Return Cards data collection
    return Card.titles
      .map((title, i) => {
        return {
          title: title,
          isEditable: Card.isEditable[i],
          icon: Card.icons[i],
          content: Card.content[i],
          link: Card.links[i],
        } as CardData;
      })
      .filter((card: CardData) => card.content);
    //#endregion
  }

  private getBclStatus(): Observable<{ statusCode: string; bclLocked: boolean }> {
    return this.profileService.apiCreds$.pipe(
      switchMap(cred => {
        return this.http.post<{ statusCode: string; bclLocked: boolean }>(
          getBclStatus,
          JSON.stringify({ bclCode: cred.bcl }),
          { headers: { 'content-type': 'application/json' } }
        );
      }),
      take(1)
    );
  }

  public canEdit(): Observable<{ canEdit: boolean; bclLockStatus: boolean }> {
    return forkJoin({
      allowEditToggle: this.profileService.fetchEditPersonalAllowed(),
      bclStatus: this.getBclStatus(),
    }).pipe(
      switchMap(c =>
        of({
          canEdit: c.allowEditToggle && !c.bclStatus.bclLocked,
          bclLockStatus: c.bclStatus.bclLocked,
        })
      )
    );
  }
}
